/**
 * THE SANCTIONS LIST ITSELF, READ BACK — AND A NAME TAKEN OFF IT WITH PROOF.
 *
 * The founder, 14 September 2026, from the Portal:
 *
 *   *"I see that I can add a name to the sanctioned list, but there's nowhere
 *    for me to view all the names that were added historically, and remove any
 *    names — WITH PROOF, THAT MUST be attached."*
 *
 * ---------------------------------------------------------------------------
 * IT WAS NOT A MISSING SCREEN. IT WAS A MISSING FUNCTION.
 *
 * Re-measured before this was written rather than trusted from the entry that
 * shelved it: `fn_screen_against_sanctions` MATCHES a name against the list and
 * never enumerates it, `fn_add_sanctions_list_entries` writes to it, and
 * **nothing removed from it**. The table has carried `removed_by_admin_id` and
 * `removed_at` since script 29 and nothing has ever written either — removal
 * was designed and not built. So no amount of screen-writing would have reached
 * it; script 359 is the pair of functions this mounts on.
 *
 * ---------------------------------------------------------------------------
 * WHY REMOVAL DEMANDS TWO THINGS AND NOT ONE
 *
 * The REASON is Canon 4.2 — why this desk is doing it, in their own words, on
 * the row for ever. The NOTICE REFERENCE is the PROOF he asked for: a delisting
 * is an official act with its own notice, exactly as the addition was evidenced
 * by the notice the names came off. A removal with a reason and no notice is an
 * opinion about a sanctioned person, which is the thing that must not be
 * possible.
 *
 * **A FILE UPLOAD IS NOT BUILT, and saying so here is the honest sentence.** If
 * "attached" means a scanned document on the entry, that is a bucket, a policy
 * and a reader. What exists today refuses a removal with no evidence at all.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS DELETED, AND THE SCREEN SAYS SO BEFORE SOMEBODY PRESSES
 *
 * A name that was on a sanctions list and came off is a fact somebody may have
 * to answer for years later. The entry stays; its status becomes `delisted` and
 * the row carries who took it off, when, why and on what notice. Screening
 * filters on `active`, so it stops matching without being erased — and the
 * history toggle below is the only way anybody ever sees it again.
 *
 * Every refusal this screen describes is the database's, not this file's.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, LoadingPanel, Notice, PrimaryCTA, QuietButton,
  Field, textInputStyle,
} from '../ui';
import { sanctionsListEntries, removeFromSanctionsList } from '../lib/sanctions';

const Line = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 14, padding: '6px 0', fontFamily: SANS, fontSize: 12.5 }}>
    <span style={{ width: 132, flexShrink: 0, color: O.inkMuted }}>{label}</span>
    <span style={{ color: O.ink, minWidth: 0 }}>{children || '—'}</span>
  </div>
);

/** One entry, and the one thing that can be done to it. */
function Entry({ row, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const off = row.status !== 'active';
  // BOTH, OR NEITHER. The database refuses each of them separately; the screen
  // states that before somebody types a paragraph and has it thrown away.
  const ready = reason.trim().length > 0 && notice.trim().length > 0;

  const remove = async () => {
    setBusy(true);
    setProblem(null);
    const res = await removeFromSanctionsList({
      entryId: row.entry_id, reason: reason.trim(), noticeReference: notice.trim(),
    });
    setBusy(false);
    if (res.error) { setProblem(res.error); return; }
    onRemoved();
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, marginBottom: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: O.ink, margin: 0 }}>
            {row.full_name}
          </p>
          {row.designation && (
            <p style={{
              fontFamily: SANS, fontSize: 12, color: O.inkMuted, margin: '4px 0 0', lineHeight: 1.6,
            }}>{row.designation}</p>
          )}
        </div>
        {/* On the list, or off it. Never colour alone — the word carries it. */}
        <StatusPill tone={off ? 'quiet' : 'critical'}>
          {off ? 'Off the list' : 'On the list'}
        </StatusPill>
      </div>

      <div style={{ borderTop: `1px solid ${O.hairline}`, paddingTop: 8 }}>
        <Line label="Date of birth">{row.date_of_birth}</Line>
        <Line label="Nationality">{row.nationality}</Line>
        <Line label="Listed on">{row.listed_on}</Line>
        <Line label="Added on notice">{row.source_notice_reference}</Line>
        <Line label="Added by">{row.added_by}</Line>
      </div>

      {off ? (
        // THE HISTORY, WHICH IS THE WHOLE POINT OF NOT DELETING. A removed entry
        // that showed only a name and a greyed-out pill would be worse than no
        // record at all: it would look like the list had forgotten.
        <div style={{
          marginTop: 10, borderTop: `1px solid ${O.hairline}`, paddingTop: 8,
        }}>
          <Line label="Taken off by">{row.removed_by}</Line>
          <Line label="Taken off">
            {row.removed_at ? String(row.removed_at).slice(0, 10) : null}
          </Line>
          <Line label="On notice">{row.removal_notice_reference}</Line>
          <Line label="Why">{row.removal_reason}</Line>
        </div>
      ) : open ? (
        <div style={{ marginTop: 12, borderTop: `1px solid ${O.hairline}`, paddingTop: 14 }}>
          {problem && <Notice tone="critical">{problem}</Notice>}

          <Field
            label="The delisting notice"
            htmlFor={`notice-${row.entry_id}`}
            hint="The proof. A name comes off this list on a notice, the same way it went on."
          >
            <input
              id={`notice-${row.entry_id}`}
              type="text"
              value={notice}
              onChange={(e) => setNotice(e.target.value)}
              placeholder="FIC/TFS/2026-11-DELIST"
              style={textInputStyle(false)}
            />
          </Field>

          <div style={{ marginTop: 12 }}>
            <Field
              label={`Why ${row.full_name} is coming off`}
              htmlFor={`reason-${row.entry_id}`}
              hint="This stays on the entry. Whoever answers for this decision years from now reads what you write here."
            >
              <textarea
                id={`reason-${row.entry_id}`}
                value={reason}
                rows={3}
                onChange={(e) => setReason(e.target.value)}
                style={{ ...textInputStyle(false), resize: 'vertical' }}
              />
            </Field>
          </div>

          {/* Two buttons that could sit side by side, do — same size, filling
              the row. */}
          <div style={{ display: 'flex', gap: 9, marginTop: 14 }}>
            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex' }}>
              <QuietButton
                onClick={() => { setOpen(false); setProblem(null); }}
                style={{ width: '100%', justifyContent: 'center' }}
              >Leave it on</QuietButton>
            </div>
            <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex' }}>
              <PrimaryCTA
                onClick={remove}
                busy={busy}
                disabled={!ready || busy}
                style={{ width: '100%' }}
              >{busy ? 'Taking it off…' : 'Take this name off'}</PrimaryCTA>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          <QuietButton onClick={() => setOpen(true)}>
            Take this name off the list
          </QuietButton>
        </div>
      )}
    </Card>
  );
}

/**
 * `writes` is a counter the section above bumps when an upload lands. It is a
 * prop rather than a shared fetch because the two halves ask the database
 * different questions — but a desk that has just added five names and sees the
 * old list has no way to tell whether the write reached the people it meant.
 */
export default function SanctionsList({ writes = 0 }) {
  const [rows, setRows] = useState(undefined);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(false);

  const load = useCallback(async (includeRemoved) => {
    setRows(undefined);
    setError(null);
    const res = await sanctionsListEntries({ includeRemoved });
    if (res.error) { setError(res.error); setRows([]); return; }
    setRows(res.data);
  }, []);

  useEffect(() => { load(history); }, [history, load, writes]);

  return (
    <Card style={{ marginBottom: 18 }}>
      <h3 style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 6px' }}>
        The sanctions list
      </h3>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, color: O.inkMuted,
        margin: '0 0 14px', lineHeight: 1.7, maxWidth: 620,
      }}>
        Every name here is screened against every person who signs up. Taking one off
        needs the delisting notice it comes off on, and nothing is ever deleted — a
        name that came off stays on this screen, under
        <strong style={{ color: O.ink }}> Everything ever listed</strong>, with who took
        it off and why.
      </p>

      {/* Two buttons in a row, same size, filling it. */}
      <div style={{ display: 'flex', gap: 9, marginBottom: 16 }}>
        {[
          [false, 'On the list now'],
          [true, 'Everything ever listed'],
        ].map(([key, label]) => {
          const on = history === key;
          return (
            <div key={label} style={{ flex: '1 1 0', minWidth: 0, display: 'flex' }}>
              {on
                ? <PrimaryCTA onClick={() => setHistory(key)} style={{ width: '100%' }}>{label}</PrimaryCTA>
                : <QuietButton onClick={() => setHistory(key)} style={{ width: '100%', justifyContent: 'center' }}>{label}</QuietButton>}
            </div>
          );
        })}
      </div>

      {error && <Notice tone="critical">{error}</Notice>}

      {rows === undefined ? (
        <LoadingPanel message="Reading the sanctions list…" />
      ) : rows.length === 0 ? (
        // AN EMPTY SANCTIONS LIST IS NOT A SUCCESS, and this is the one screen
        // in the Portal where that inversion holds. Screening has run against
        // this list on every signup since script 29; an empty one finds nothing
        // and is indistinguishable from working.
        <Notice tone="warning">
          {history
            ? 'No name has ever been on this list. Screening every new profile against it '
              + 'has therefore never found anything, which is not the same as there being '
              + 'nothing to find.'
            : 'No name is currently on the list. Add the names from an FIC notice above.'}
        </Notice>
      ) : (
        rows.map((r) => (
          <Entry key={r.entry_id} row={r} onRemoved={() => load(history)} />
        ))
      )}
    </Card>
  );
}
